import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { ApiError } from "../lib/api";
import {
  adminAiBackendQueryOptions,
  adminRegistryQueryOptions,
  adminScanConfigQueryOptions,
  adminSettingsQueryOptions,
  updateSetting,
} from "./queries";
import {
  REGISTRY_TABLES,
  type AiBackendResponse,
  type RegistrySlug,
  type ScanConfigResponse,
  type SystemSetting,
} from "./types";

type AdminTab = "documents" | "system";

const TABS: { id: AdminTab; label: string }[] = [
  { id: "documents", label: "Documents" },
  { id: "system", label: "System" },
];

export function AdminPage() {
  const [tab, setTab] = useState<AdminTab>("documents");
  return (
    <div className="page">
      <h1>Admin</h1>
      <div role="tablist" className="tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={tab === t.id ? "btn" : "btn btn--ghost"}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "documents" ? <DocumentsTab /> : <SystemTab />}
    </div>
  );
}

function DocumentsTab() {
  return (
    <section>
      <h2>Document vocabulary</h2>
      <p className="empty-state">
        Project types, lifecycle stages, document roles + sub-types, the
        per-type/stage requirement matrix, and folder skip rules.
        Read-only; ADR-012 makes the registry append-only — changes must
        flow through a new migration.
      </p>
      <RegistrySection />
    </section>
  );
}

function SystemTab() {
  return (
    <>
      <section>
        <h2>Deployment &amp; onboarding</h2>
        <p className="empty-state">
          Multi-step operator workflows live on their own pages. Link
          here keeps Admin the navigational hub without absorbing the
          full workflow trees.
        </p>
        <DeploymentLinks />
      </section>
      <section>
        <h2>AI backend</h2>
        <p className="empty-state">
          Which AI inference backend the worker resolved at boot. Set
          one of <code>ANTHROPIC_API_KEY</code>, <code>OLLAMA_BASE_URL</code>,
          or <code>OPENAI_COMPATIBLE_BASE_URL</code> in <code>.env</code>;
          first match wins. Changes require a worker restart.
        </p>
        <AiBackendSection />
      </section>
      <section>
        <h2>Scan roots</h2>
        <p className="empty-state">
          Parent paths the folder scanner walks. Each immediate
          subdirectory under <code>FOLDER_SCAN_ROOT</code> becomes a
          candidate project folder. Configured via <code>.env</code>;
          changes require a worker restart.
        </p>
        <ScanConfigSection />
      </section>
      <section>
        <h2>Runtime settings</h2>
        <p className="empty-state">
          Cadences and other runtime-tunable values. Previously edited in{" "}
          <code>.env</code>; changes here persist to the database.
        </p>
        <SettingsSection />
      </section>
    </>
  );
}

function DeploymentLinks() {
  const links = [
    {
      to: "/bootstrap",
      label: "Register bootstrap",
      description:
        "Excel import wizard, folder advisor, register-source conflict resolver (US5).",
    },
    {
      to: "/crm-profiles",
      label: "CRM adapter profiles",
      description:
        "Schema-discovery proposals, PR-based apply, drift alerts (US6).",
    },
  ] as const;
  return (
    <div className="card-grid">
      {links.map((l) => (
        <Link key={l.to} to={l.to} className="card-link">
          <div className="card-link__title">{l.label}</div>
          <div className="card-link__desc">{l.description}</div>
        </Link>
      ))}
    </div>
  );
}

function ScanConfigSection() {
  const { data, isLoading, error } = useQuery(adminScanConfigQueryOptions);
  if (isLoading) return <p className="empty-state">Loading…</p>;
  if (error) return <ErrorBlock error={error} />;
  if (!data) return null;
  return <ScanConfigCard config={data} />;
}

function ScanConfigCard({ config }: { config: ScanConfigResponse }) {
  return (
    <div className="panel">
      <dl className="kv-grid">
        <dt>
          <code>FOLDER_SCAN_ROOT</code>
        </dt>
        <dd>
          {config.folder_scan_root ? (
            <code>{config.folder_scan_root}</code>
          ) : (
            <span className="subtle">
              (not configured -- register-sync runs CRM-only)
            </span>
          )}
        </dd>
        <dt>
          <code>WORKBOOK_ALLOWED_SHARE_ROOTS</code>
        </dt>
        <dd>
          {config.workbook_allowed_share_roots.length === 0 ? (
            <span className="subtle">
              (empty -- workbook reader has no allowlist)
            </span>
          ) : (
            <ul className="path-list">
              {config.workbook_allowed_share_roots.map((root) => (
                <li key={root}>{root}</li>
              ))}
            </ul>
          )}
        </dd>
        <dt>Discovered project folders</dt>
        <dd>{config.discovered_folders_count}</dd>
      </dl>
    </div>
  );
}

function AiBackendSection() {
  const { data, isLoading, error } = useQuery(adminAiBackendQueryOptions);
  if (isLoading) return <p className="empty-state">Loading…</p>;
  if (error) return <ErrorBlock error={error} />;
  if (!data) return null;
  return <AiBackendCard backend={data} />;
}

function AiBackendCard({ backend }: { backend: AiBackendResponse }) {
  const tone =
    backend.mode === "rule_based"
      ? "danger"
      : backend.mode === "anthropic"
        ? "success"
        : "neutral";
  const labels: Record<AiBackendResponse["mode"], string> = {
    anthropic: "Anthropic",
    ollama: "Ollama",
    openai_compatible: "OpenAI-compatible",
    rule_based: "Rule-based fixtures",
  };
  return (
    <div className="panel">
      <div className="row">
        <span className={`pill pill--${tone}`}>{labels[backend.mode]}</span>
        <span className="subtle">{backend.description}</span>
      </div>
      <dl className="kv-grid" style={{ marginTop: "var(--space-3)" }}>
        <dt>Model</dt>
        <dd>
          <code>{backend.model ?? "(none — fixture mode)"}</code>
        </dd>
        <dt>Base URL</dt>
        <dd>
          <code>{backend.base_url ?? "(n/a)"}</code>
        </dd>
        <dt>API key</dt>
        <dd>{backend.api_key_set ? "set" : "not set"}</dd>
      </dl>
      <details style={{ marginTop: "var(--space-2)" }}>
        <summary>Environment variables</summary>
        <dl className="kv-grid" style={{ marginTop: "var(--space-2)" }}>
          {Object.entries(backend.env_vars).map(([name, present]) => (
            <span key={name} style={{ display: "contents" }}>
              <dt>
                <code>{name}</code>
              </dt>
              <dd>{present ? "set" : "unset"}</dd>
            </span>
          ))}
        </dl>
      </details>
    </div>
  );
}

function SettingsSection() {
  const { data, isLoading, error } = useQuery(adminSettingsQueryOptions);
  if (isLoading) return <p className="empty-state">Loading…</p>;
  if (error) return <ErrorBlock error={error} />;

  const settings = data?.settings ?? [];
  if (settings.length === 0) {
    return <p className="empty-state">No runtime settings configured.</p>;
  }
  return (
    <div>
      {settings.map((s) => (
        <SettingRow key={s.key} setting={s} />
      ))}
    </div>
  );
}

function SettingRow({ setting }: { setting: SystemSetting }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string>(String(setting.value ?? ""));
  const [localError, setLocalError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (raw: string) => {
      let value: unknown = raw;
      if (setting.value_type === "integer") {
        const n = Number(raw);
        if (!Number.isFinite(n) || !Number.isInteger(n)) {
          return Promise.reject(new Error("Must be an integer"));
        }
        value = n;
      } else if (setting.value_type === "boolean") {
        value = raw === "true";
      }
      return updateSetting(setting.key, value);
    },
    onSuccess: (updated) => {
      setLocalError(null);
      setDraft(String(updated.value));
      setSavedAt(new Date().toLocaleTimeString());
      qc.invalidateQueries({ queryKey: ["admin", "settings"] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { detail?: string } | null;
        setLocalError(body?.detail ?? err.message);
      } else if (err instanceof Error) {
        setLocalError(err.message);
      } else {
        setLocalError(String(err));
      }
    },
  });

  const bounds = useMemo(() => {
    const parts: string[] = [];
    if (setting.min_value !== null) parts.push(`min ${setting.min_value}`);
    if (setting.max_value !== null) parts.push(`max ${setting.max_value}`);
    return parts.join(", ");
  }, [setting.min_value, setting.max_value]);

  const dirty = String(setting.value) !== draft;

  return (
    <div className="panel" style={{ marginBottom: "var(--space-2)" }}>
      <label
        htmlFor={`setting-${setting.key}`}
        style={{ display: "block", fontWeight: 600 }}
      >
        {setting.key}
      </label>
      <p className="subtle" style={{ margin: "var(--space-1) 0 var(--space-2)", fontSize: 13 }}>
        {setting.description}
        {bounds ? ` (${bounds})` : ""}
      </p>
      <div className="form-row">
        <input
          id={`setting-${setting.key}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          type={setting.value_type === "integer" ? "number" : "text"}
          disabled={mutation.isPending}
        />
        <button
          type="button"
          className="btn"
          disabled={!dirty || mutation.isPending}
          onClick={() => mutation.mutate(draft)}
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </button>
        {savedAt ? (
          <span className="status status--success">saved at {savedAt}</span>
        ) : null}
      </div>
      {localError ? (
        <p className="alert" role="alert" style={{ marginTop: "var(--space-2)" }}>
          {localError}
        </p>
      ) : null}
      <p className="helper-text">
        last updated {new Date(setting.updated_at).toLocaleString()}
        {setting.updated_by ? ` by ${setting.updated_by}` : ""}
      </p>
    </div>
  );
}

function RegistrySection() {
  const [active, setActive] = useState<RegistrySlug>(REGISTRY_TABLES[0].slug);
  return (
    <div>
      <div role="tablist" className="tabs tabs--inline">
        {REGISTRY_TABLES.map((t) => (
          <button
            key={t.slug}
            type="button"
            role="tab"
            aria-selected={active === t.slug}
            className={active === t.slug ? "btn" : "btn btn--ghost"}
            onClick={() => setActive(t.slug)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <RegistryTable slug={active} />
    </div>
  );
}

function RegistryTable({ slug }: { slug: RegistrySlug }) {
  const { data, isLoading, error } = useQuery(adminRegistryQueryOptions(slug));
  if (isLoading) return <p className="empty-state">Loading…</p>;
  if (error) return <ErrorBlock error={error} />;

  const rows = data?.rows ?? [];
  const first = rows[0];
  if (!first) {
    return <p className="empty-state">No rows in {data?.table ?? slug}.</p>;
  }
  // Hide UUID id columns -- they're noise for an admin viewer (operators
  // identify registry rows by their natural keys: code, slug, pattern).
  // The id is still on the wire if a future feature needs it.
  const columns = Object.keys(first).filter((c) => c !== "id");
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c}>{formatCell(r[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Detects strings the API serialised via `datetime.isoformat()` —
// e.g. "2026-04-22T09:06:56.671847+00:00" — so registry timestamps
// render as the operator's local time rather than raw ISO. Date-only
// values (no `T`) are left alone.
const ISO_TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "string" && ISO_TIMESTAMP_RE.test(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  }
  return String(value);
}

function ErrorBlock({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.status === 403) {
    return (
      <p className="alert" role="alert">
        You need the <code>project_owner</code> role to access admin settings.
      </p>
    );
  }
  const message = error instanceof Error ? error.message : String(error);
  return (
    <p className="alert" role="alert">
      Failed to load: {message}
    </p>
  );
}
